/**
 * Collect-in-store, as the shop shows it (worker/src/shop-collect.ts decides it).
 *
 * 🔴 Every "Collect today at …" / "Collect from … tomorrow" sentence is a link to the collection
 * shop's map, in a NEW TAB — the customer is mid-basket and must not lose it. The shop's name,
 * address and map link come from the server's settings; nothing here names a place.
 */
import { useEffect, useState } from "react";
import type { CollectAnswer } from "./types";

export function CollectLink({ label, mapUrl, className }: { label: string; mapUrl: string | null | undefined; className?: string }) {
  if (!mapUrl) return <span className={className}>{label}</span>;
  return (
    <a className={className} href={mapUrl} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

/**
 * When this basket can be collected. Debounced and aborted like the delivery quote, so a quantity
 * tapped five times asks once and a stale answer never lands last.
 */
export function useCollect(lines: Array<{ variantId: string; quantity: number }>): CollectAnswer | null {
  const [answer, setAnswer] = useState<CollectAnswer | null>(null);
  const key = lines.map((l) => `${l.variantId}:${l.quantity}`).join(",");
  useEffect(() => {
    if (!key) {
      setAnswer(null);
      return;
    }
    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const r = await fetch("/api/shop/collect", {
          method: "POST",
          headers: { "content-type": "application/json" },
          signal: ac.signal,
          body: JSON.stringify({ items: lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })) }),
        });
        const d = r.ok ? ((await r.json()) as CollectAnswer) : null;
        if (!ac.signal.aborted) setAnswer(d);
      } catch {
        if (!ac.signal.aborted) setAnswer(null);
      }
    }, 300);
    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
    // `key` is the lines' identity; `lines` itself is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return answer;
}
